import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
} from '@mui/icons-material';
import type { Camera } from '../../services/camerasApi';
import ContainedMediaOverlay from './ContainedMediaOverlay';

type StreamState = { url: string | null; loading: boolean; error: string | null };
type ViewKind = 'live' | 'annotated';
type CameraUnavailableState = { title: string; message: string; detail?: string } | null;

export type LiveViewFullscreenDialogProps = {
  open: boolean;
  fullScreen: boolean;
  title: string;
  camera: Camera | null;
  streamKey: string | null;
  viewKind: ViewKind;
  stream: StreamState | null | undefined;
  unavailableState: CameraUnavailableState;
  zoom: number;
  pan: { x: number; y: number };
  stoppedStreamKeys: Set<string>;
  overlay?: React.ReactNode;
  /** When set (Annotated Evidence mode), render this instead of the MJPEG stream. */
  evidenceContent?: React.ReactNode;
  onClose: () => void;
  onToggleFullScreen: (next: boolean) => void;
  onZoomChange: (next: number) => void;
  onPanChange: (next: { x: number; y: number }) => void;
  onResetZoomPan: () => void;
  onWheel: React.WheelEventHandler<HTMLDivElement>;
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  onStreamLoad: () => void;
  onStreamError: () => void;
  onStop: () => void;
  onStart: () => void;
};

export function LiveViewFullscreenDialog({
  open,
  fullScreen,
  title,
  camera,
  streamKey,
  viewKind: _viewKind,
  stream,
  unavailableState,
  zoom,
  pan,
  stoppedStreamKeys,
  overlay,
  evidenceContent,
  onClose,
  onToggleFullScreen,
  onZoomChange,
  onPanChange: _onPanChange,
  onResetZoomPan,
  onWheel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onStreamLoad,
  onStreamError,
  onStop,
  onStart,
}: LiveViewFullscreenDialogProps) {
  const isStopped = streamKey ? stoppedStreamKeys.has(streamKey) : true;
  const showEvidence = Boolean(evidenceContent) && !unavailableState;
  const iconSx = fullScreen ? { color: '#fff' } : undefined;

  const toolbar = (
    <Box
      data-testid="live-view-viewer-toolbar"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        gap: 0.5,
        ...(fullScreen
          ? {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 3,
              px: 0.75,
              py: 0.25,
              bgcolor: 'rgba(0,0,0,0.58)',
            }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              size="small"
              aria-label="Zoom out"
              onClick={() => onZoomChange(Math.max(1, zoom * 0.9))}
              disabled={zoom <= 1}
              sx={iconSx}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton size="small" aria-label="Zoom in" onClick={() => onZoomChange(Math.min(4, zoom * 1.1))} sx={iconSx}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset zoom/pan">
          <IconButton size="small" aria-label="Reset zoom" onClick={onResetZoomPan} sx={iconSx}>
            <ResetZoomIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ color: fullScreen ? 'rgba(255,255,255,0.82)' : 'text.secondary', minWidth: 40 }}>
          {Math.round(zoom * 100)}%
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
        {!showEvidence && streamKey && camera ? (
          <>
            <Tooltip title="Stop">
              <span>
                <IconButton
                  size="small"
                  aria-label="Stop"
                  onClick={onStop}
                  disabled={isStopped || stream?.loading || Boolean(unavailableState)}
                  sx={iconSx}
                >
                  <StopIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Start">
              <span>
                <IconButton
                  size="small"
                  aria-label="Start"
                  onClick={onStart}
                  disabled={!isStopped || stream?.loading || Boolean(unavailableState)}
                  sx={iconSx}
                >
                  <PlayIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : null}
        {!fullScreen ? (
          <Tooltip title="Enter fullscreen">
            <IconButton size="small" aria-label="Enter fullscreen" onClick={() => onToggleFullScreen(true)}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Exit fullscreen">
            <IconButton size="small" aria-label="Exit fullscreen" onClick={() => onToggleFullScreen(false)} sx={iconSx}>
              <FullscreenExitIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      sx={fullScreen ? { '& .MuiDialog-paper': { display: 'flex', flexDirection: 'column' } } : undefined}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexShrink: 0,
          py: fullScreen ? 0.75 : undefined,
          minHeight: fullScreen ? 48 : undefined,
          pr: 1,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        <IconButton onClick={onClose} aria-label="Close live view" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: fullScreen ? 0 : 3,
          display: 'flex',
          flexDirection: 'column',
          flex: fullScreen ? 1 : undefined,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            flex: fullScreen ? 1 : undefined,
            height: fullScreen ? '100%' : { xs: 360, sm: 520, md: 640 },
            minHeight: fullScreen ? 0 : undefined,
            bgcolor: 'black',
            borderRadius: fullScreen ? 0 : 1,
            overflow: 'hidden',
            touchAction: 'none',
          }}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {unavailableState && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                backgroundColor: '#f3f4f6',
              }}
            >
              <Alert severity="info" sx={{ width: '100%', maxWidth: 520 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {unavailableState.title}
                </Typography>
                <Typography variant="body2">{unavailableState.message}</Typography>
                {unavailableState.detail ? (
                  <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                    {unavailableState.detail}
                  </Typography>
                ) : null}
              </Alert>
            </Box>
          )}
          {showEvidence ? (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100%',
                height: '100%',
                transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
              }}
              data-testid="live-view-fullscreen-evidence"
            >
              {evidenceContent}
            </Box>
          ) : null}
          {!showEvidence && stream?.loading && !unavailableState ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={48} />
            </Box>
          ) : null}
          {!showEvidence && stream?.error && !unavailableState ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
              }}
            >
              <Alert severity="warning" sx={{ width: '100%' }}>
                {stream.error}
              </Alert>
            </Box>
          ) : null}
          {!showEvidence && stream?.url && !stream.loading && !stream.error && !unavailableState ? (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100%',
                height: '100%',
                transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
              }}
            >
              <ContainedMediaOverlay
                src={stream.url}
                alt={camera?.name || camera?.camera_id || 'camera'}
                imageStyle={{
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                overlay={overlay}
                onLoad={onStreamLoad}
                onError={onStreamError}
              />
            </Box>
          ) : null}
          {fullScreen ? toolbar : null}
        </Box>
      </DialogContent>
      {fullScreen ? null : (
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            px: 2,
            py: 0.75,
            minHeight: 48,
          }}
        >
          {toolbar}
        </DialogActions>
      )}
    </Dialog>
  );
}
